import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Stack,
} from "@mui/material";
import { useSupplies } from "../hooks/useSupplies";

export const SupplieCard = ({
  supply,
  selectedSupplies,
  onSelect,
  onPriceChange,
}) => {
  const isSelectedProp = useMemo(
    () => selectedSupplies.some((item) => Number(item.id) === Number(supply.id)),
    [selectedSupplies, supply.id]
  );

  const [isSelectedLocal, setIsSelectedLocal] = useState(isSelectedProp);
  useEffect(() => setIsSelectedLocal(isSelectedProp), [isSelectedProp]);

  const selectedEntry = useMemo(
    () => selectedSupplies.find((item) => Number(item.id) === Number(supply.id)),
    [selectedSupplies, supply.id]
  );

  const basePriceNumber = useMemo(() => {
    const fromSelected = selectedEntry?.price;
    const fromSupply = supply?.price;
    const asNumber =
      typeof fromSelected !== "undefined"
        ? Number(fromSelected)
        : Number(fromSupply);
    return Number.isFinite(asNumber) ? asNumber : 0;
  }, [selectedEntry, supply]);

  const [localPrice, setLocalPrice] = useState(basePriceNumber.toFixed(2));
  const [pendingPrice, setPendingPrice] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { patchSuppliesPrice } = useSupplies();

  useEffect(() => {
    setLocalPrice(basePriceNumber.toFixed(2));
  }, [basePriceNumber]);

  const handleBlur = () => {
    const newPrice = parseFloat(localPrice);
    if (!Number.isFinite(newPrice) || newPrice === basePriceNumber) {
      setLocalPrice(basePriceNumber.toFixed(2));
      return;
    }
    setPendingPrice(newPrice);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (pendingPrice == null) return;
    onPriceChange?.(supply.id, pendingPrice);
    try {
      await patchSuppliesPrice(supply.id, pendingPrice);
    } catch (e) {
      setLocalPrice(basePriceNumber.toFixed(2));
    }
    setConfirmOpen(false);
  };

  const handleCancelPrice = () => {
    setLocalPrice(basePriceNumber.toFixed(2));
    setPendingPrice(null);
    setConfirmOpen(false);
  };

  const handleToggleSelect = () => {
    setIsSelectedLocal((v) => !v);
    onSelect?.(supply);            
  };

  return (
    <Box>
      <Card
        onClick={handleToggleSelect}
        sx={{
          userSelect: "none",
          cursor: "pointer",
          borderRadius: 2,
          boxShadow: isSelectedLocal ? 6 : 1,
          border: isSelectedLocal ? "2px solid #1976d2" : "1px solid var(--fx-surface-border, #E2E8F0)",
          backgroundColor: isSelectedLocal ? "var(--fx-colors-info-50, #EFF6FF)" : "var(--fx-surface-base, #FFFFFF)",
          transition: "box-shadow 180ms ease, border-color 180ms ease, background-color 180ms ease",
        }}
      >
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Box display="flex" alignItems="center" minWidth={0}>
              {(supply?.image || supply?.supply?.image) && (
                <Box
                  component="img"
                  src={supply?.image || supply?.supply?.image}
                  alt={supply?.name || ""}
                  sx={{
                    width: 64,
                    height: 64,
                    objectFit: "cover",
                    borderRadius: 1,
                    mr: 2,
                    flexShrink: 0,
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              )}

              <Box minWidth={0}>
                <Typography variant="h6" noWrap title={supply?.name}>
                  {supply?.name}
                </Typography>
                {!!supply?.type && (
                  <Chip
                    label={supply.type}
                    size="small"
                    color="primary"
                    sx={{ mt: 0.5 }}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </Box>
            </Box>

            <Box width={120}>
              <TextField
                label="Price"
                value={localPrice}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*\.?\d{0,2}$/.test(val)) setLocalPrice(val);
                }}
                onBlur={handleBlur}
                onClick={(e) => e.stopPropagation()}
                inputProps={{
                  inputMode: "decimal",
                  sx: { textAlign: "right", paddingRight: 1 },
                }}
                size="small"
                fullWidth
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog hideBackdrop={true} sx={{ zIndex: 999999999 }} open={confirmOpen} onClose={handleCancelPrice}>
        <DialogTitle>Confirm Price Change</DialogTitle>
        <DialogContent>
          <Typography>
            Change “{supply?.name}” from ${basePriceNumber.toFixed(2)} to ${pendingPrice?.toFixed(2)}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelPrice}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
